import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'

import { ProfileAlert } from '@modules/alerts/types'
import ProfileHeader from '@modules/profile/ProfileHeader'
import { DEFAULT_DELEGATE_LOGO_SRC } from '@modules/profile/constants'
import { DELEGATE_NON_EMPTY_STATE_FIELDS } from '@modules/profile/profileConfig'
import { Delegate, DelegateHistoryProfile, ProfileType } from '@modules/search/types'
import { useScreenDimensions } from '@shared/hooks/useScreenDimensions'

import CompanyCard from '../CompanyCard'
import ProfileDetails from '../ProfileDetails'
import {
  ProfileContent,
  ProfileContentWrapper,
  ProfileEmptySection,
  ProfileEmptySectionMessage,
  ProfileLogoWrapper,
  ProfileMainColumn,
  ProfileOtherInfo,
  ProfileOuterWrapper,
  ProfileSkeleton,
  ProfileTypeLogo,
  ProfileWrapper,
  ProfileWrapperInner,
} from '../index.styles'

interface DelegateProfileProps {
  profile?: DelegateHistoryProfile
  isError?: boolean
  isFetching?: boolean
  profileAlert?: ProfileAlert
}

const DelegateProfile = (props: DelegateProfileProps) => {
  const { profile, isError, isFetching, profileAlert } = props
  const { latest: delegateData, events } = profile || ({} as DelegateHistoryProfile)
  const { t } = useTranslation()
  const { isDesktop } = useScreenDimensions()
  const isProfileEmpty = useMemo(
    () =>
      !delegateData ||
      DELEGATE_NON_EMPTY_STATE_FIELDS.every(field => {
        const value = delegateData[field as keyof Delegate]

        return Array.isArray(value) ? !value.length : !value
      }),
    [delegateData],
  )

  return (
    <ProfileOuterWrapper>
      <ProfileWrapper>
        {isError && (
          <ProfileWrapperInner>
            <ProfileContentWrapper isError>{t(translationKeys.FETCHING_ERROR)}</ProfileContentWrapper>
          </ProfileWrapperInner>
        )}
        {isFetching && (
          <ProfileWrapperInner>
            <ProfileContentWrapper isFetching>
              <ProfileContent>
                <ProfileMainColumn>
                  <ProfileSkeleton animation="wave" />
                  <ProfileSkeleton animation="wave" />
                  <ProfileSkeleton animation="wave" />
                  <ProfileSkeleton animation="wave" />
                  <ProfileSkeleton animation="wave" />
                  <ProfileSkeleton animation="wave" />
                  <ProfileSkeleton animation="wave" />
                </ProfileMainColumn>
                <ProfileOtherInfo>
                  <ProfileSkeleton animation="wave" />
                  <ProfileSkeleton animation="wave" />
                  <ProfileSkeleton animation="wave" />
                  <ProfileSkeleton animation="wave" />
                  <ProfileSkeleton animation="wave" />
                  <ProfileSkeleton animation="wave" />
                  <ProfileSkeleton animation="wave" />
                </ProfileOtherInfo>
              </ProfileContent>
            </ProfileContentWrapper>
          </ProfileWrapperInner>
        )}
        {delegateData && !isFetching && (
          <>
            <ProfileWrapperInner>
              <ProfileContentWrapper>
                <ProfileHeader
                  profile={profile as DelegateHistoryProfile}
                  profileJobTitle={delegateData.jobTitle}
                  profileDivision={delegateData.department}
                  profileName={{ title: delegateData.title, firstName: delegateData.firstName, lastName: delegateData.lastName }}
                  updatedAt={delegateData.updatedAt}
                  companyId={delegateData.companyId}
                  profileType={ProfileType.delegate}
                  profileAlert={profileAlert}
                  events={events}
                />
                <ProfileContent>
                  <ProfileMainColumn>
                    {isProfileEmpty ? (
                      <ProfileEmptySection>
                        <ProfileEmptySectionMessage variant="h3" data-testid="profile-empty-section">
                          <ProfileLogoWrapper emptySection>
                            <ProfileTypeLogo component="img" src={`${DEFAULT_DELEGATE_LOGO_SRC}`} />
                          </ProfileLogoWrapper>
                          {t(translationKeys.PROFILE_SECTION_EMPTY)}
                        </ProfileEmptySectionMessage>
                      </ProfileEmptySection>
                    ) : (
                      <ProfileDetails profileData={delegateData} profileType={ProfileType.delegate} profileAreaName="mainInfo" />
                    )}
                  </ProfileMainColumn>
                  <ProfileOtherInfo>
                    <ProfileDetails
                      profileData={delegateData}
                      profileType={ProfileType.delegate}
                      profileAreaName="secondColumnInfo"
                    />
                  </ProfileOtherInfo>
                </ProfileContent>
              </ProfileContentWrapper>
            </ProfileWrapperInner>
            {isDesktop && <CompanyCard companyId={delegateData.companyId} />}
          </>
        )}
      </ProfileWrapper>
    </ProfileOuterWrapper>
  )
}

export default DelegateProfile
